import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import FilterToolbar from '../../components/FilterToolbar';
import { getVendorReturns, getVendorReturn } from '../../api/vendorReturn';
import { getVendors } from '../../api/vendor';
import usePoGrnLookup from '../../hooks/usePoGrnLookup';
import { formatDate, formatQty, formatCurrency, exportToCsv } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

function hasReturnQty(row) {
  const direct = row?.total_return_quantity ?? row?.total_quantity ?? row?.return_quantity ?? row?.quantity ?? row?.qty;
  if (direct != null && direct !== '') return true;
  return normalizeList(row?.lines || row?.items).length > 0;
}

function resolveReturnQty(row) {
  const direct = row?.total_return_quantity ?? row?.total_quantity ?? row?.return_quantity ?? row?.quantity ?? row?.qty;
  if (direct != null && direct !== '') {
    return formatQty(direct);
  }
  const lines = normalizeList(row?.lines || row?.items);
  if (lines.length) {
    const sum = lines.reduce((s, l) => s + (Number(l.return_quantity ?? l.quantity ?? l.qty) || 0), 0);
    return formatQty(sum);
  }
  return '—';
}

async function enrichReturnRow(row) {
  if (hasReturnQty(row)) return row;
  const id = row.vendor_return_id || row.id;
  if (!id) return row;
  try {
    const detail = await getVendorReturn(id);
    return {
      ...row,
      lines: row.lines || row.items || detail.lines || detail.items,
      total_return_quantity:
        row.total_return_quantity
        ?? detail.total_return_quantity
        ?? detail.total_quantity,
    };
  } catch {
    return row;
  }
}

const RETURN_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'REFUND', label: 'Refund' },
  { value: 'REPLACEMENT', label: 'Replacement' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export default function VendorReturnList() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const base = url.replace('/vendor-returns', '');
  const { resolveGrnNumber } = usePoGrnLookup();
  const [returns, setReturns] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [returnType, setReturnType] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    getVendors().then(d => setVendors(normalizeList(d))).catch(() => setVendors([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (returnType) filters.return_type = returnType;
      if (status) filters.status = status;
      // API: GET /inventory/vendor-returns/
      const data = await getVendorReturns({}, filters);
      const rows = normalizeList(data);
      // List payload often omits lines / total qty — fill from detail when needed.
      const enriched = await Promise.all(rows.map(enrichReturnRow));
      setReturns(enriched);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [returnType, status]);

  useEffect(() => { load(); }, [load]);

  function resolveVendorName(vendorId) {
    if (!vendorId) return '—';
    const v = vendors.find(x => (x.vendor_id || x.id) === vendorId);
    if (!v) return vendorId;
    return v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name;
  }

  const filteredReturns = search
    ? returns.filter(r => (r.return_number || '').toLowerCase().includes(search.toLowerCase()))
    : returns;

  const columns = [
    { field: 'return_number', headerName: 'Return', width: 170 },
    { field: 'source_grn_id', headerName: 'Source GRN', width: 170,
      valueGetter: p => resolveGrnNumber(p.row.source_grn_id, p.row.grn_number) },
    { field: 'vendor_id', headerName: 'Vendor', width: 200,
      valueGetter: p => resolveVendorName(p.row.vendor_id) },
    { field: 'return_type', headerName: 'Type', width: 130 },
    { field: 'total_return_quantity', headerName: 'Qty', width: 90, align: 'right', headerAlign: 'right',
      valueGetter: p => resolveReturnQty(p.row) },
    { field: 'return_date', headerName: 'Return Date', width: 120, valueFormatter: p => formatDate(p.value) },
    { field: 'total_amount', headerName: 'Total Amount', width: 140, align: 'right', headerAlign: 'right',
      valueGetter: p => (p.row.total_amount != null ? formatCurrency(p.row.total_amount) : '—') },
    { field: 'status', headerName: 'Status', width: 140,
      renderCell: p => <StatusChip status={p.value} /> },
    { field: 'actions', headerName: '', width: 70, sortable: false,
      renderCell: (p) => {
        const returnId = p.row.vendor_return_id || p.row.id;
        return (
          <Tooltip title="View">
            <IconButton size="small" onClick={() => history.push(`${base}/vendor-return/${returnId}`)}
              sx={{ color: IPD_COLORS.primary }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      } },
  ];

  return (
    <Box>
      <PageHeader eyebrow="Material Management" title="Vendor Returns"
        description={`${returns.length} returns`}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <InvButton startIcon={<HourglassTopIcon />} variant="outlined" tone="teal" size="small"
              onClick={() => history.push(`${base}/vendor-returns/awaiting-replacement`)}>Awaiting Replacement</InvButton>
            <InvButton startIcon={<DownloadIcon />} variant="outlined" tone="teal" size="small"
              onClick={() => exportToCsv('vendor-returns.csv', filteredReturns)}>Export</InvButton>
            {can(INV_ACTIONS.VENDOR_RETURN_CREATE) && (
              <InvButton startIcon={<AddIcon />} size="small"
                onClick={() => history.push(`${base}/vendor-return/new`)}>New Return</InvButton>
            )}
          </Box>
        }
      />


      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search by return ..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FilterToolbar.Filter
          label="Return Type"
          width="wide"
          value={returnType}
          onChange={e => setReturnType(e.target.value)}
          options={RETURN_TYPE_OPTIONS}
        />
        <FilterToolbar.Filter
          label="Status"
          width="wide"
          value={status}
          onChange={e => setStatus(e.target.value)}
          options={STATUS_OPTIONS}
        />
      </FilterToolbar>

      <InvDataGrid fillHeight rows={filteredReturns} columns={columns} loading={loading} error={error} onRetry={load}
        getRowId={r => r.vendor_return_id || r.id} emptyMessage="No vendor returns found." />
    </Box>
  );
}
